import _ from 'lodash';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { COLOR_PALETTE, mediaQuery } from 'constants/ui';
import { getConditionalObject } from 'utils/data';

const mobileFeeCardStyles = {
  [mediaQuery.mobile]: {
    overflowX: 'hidden',

    '& table, & thead, & tbody, & tr, & th, & td': {
      display: 'block',
      width: '100% !important',
    },

    '& table': {
      width: '100% !important',
    },

    '& thead': {
      display: 'none',
    },

    '& tbody tr': {
      height: 'auto',
      padding: '12px',
      marginBottom: '8px',
      border: '1px solid',
      borderColor: ({ palette }) => palette.border.default,
      borderRadius: '8px',

      '&:last-of-type': {
        marginBottom: 0,
      },
    },

    '& tbody td': {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '8px',
      width: '100% !important',
      minHeight: 'auto',
      padding: '6px 0 !important',
      border: 'none !important',
      whiteSpace: 'normal !important',
      position: 'static',

      '& .truncate': {
        whiteSpace: 'normal !important',
        overflow: 'visible',
        textOverflow: 'clip',
        minWidth: 0,
      },

      '&[data-fee-label]::before': {
        content: 'attr(data-fee-label)',
        flexShrink: 1,
        minWidth: 0,
        fontWeight: 600,
        color: ({ palette }) => palette.text.secondary,
      },

      '&:first-of-type': {
        borderBottom: '1px solid !important',
        borderColor: ({ palette }) => `${palette.border.default} !important`,
        paddingBottom: '8px !important',
        marginBottom: '4px',
      },

      '&[colspan="2"]': {
        flexDirection: 'column',
        padding: '0 !important',

        '& > div': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          padding: 0,

          '& > span': {
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            width: '100% !important',
            padding: '6px 0 !important',
            height: 'auto !important',
            border: 'none !important',

            '&[data-fee-label]::before': {
              content: 'attr(data-fee-label)',
              flexShrink: 1,
              minWidth: 0,
              fontWeight: 600,
              color: ({ palette }) => palette.text.secondary,
            },

            '& .float-right': {
              float: 'none !important',
            },
          },
        },
      },

      '& .float-right': {
        float: 'none !important',
      },

      '& [data-is-empty="true"][data-is-changed="false"]': {
        display: 'block !important',

        '&::after': {
          content: '"—"',
          color: ({ palette }) => palette.text.secondary,
        },
      },
    },

    '& .financed-amount-cell': {
      minWidth: '0 !important',
    },
  },
};

export const pendingRowCellsStyles = {
  '&.table-row-pending td': {
    background: COLOR_PALETTE.ADDITIONAL[1],
  },
};

export const deletedPendingRowStyles = {
  [`tbody tr.table-row-pending-${PendingChangesActionType.DELETED}::after`]: {
    position: 'absolute',
    content: "''",
    width: 'calc(100% - 40px)',
    top: '50%',
    bottom: 0,
    left: '20px',
    right: '20px',
    height: '1px',
    zIndex: 2,
    background: COLOR_PALETTE.ADDITIONAL[2],
  },
};

const COLUMN_WIDTH = 300;

const MERGED_COLUMN_WIDTH = 540;

const getStickyColumnSelector = ({ isOpened, isFeeProviderColumnVisible }) =>
  [
    '&:nth-of-type(1)',
    isFeeProviderColumnVisible && '&:nth-of-type(2)',
    isOpened && '&:nth-last-of-type(1)',
  ]
    .filter(Boolean)
    .join(', ');

const stickyColumnStyles = {
  position: 'sticky',
  background: ({ palette }) => palette.background.paper,
  overflow: 'hidden',
  zIndex: 1,
  borderRight: 'none !important',
  borderLeft: 'none !important',

  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100px',
    width: '1px',
    backgroundColor: ({ palette }) => palette.border.default,
  },
};

const getColumnOffsetStyles = ({ isOpened, isFeeProviderColumnVisible }) => ({
  '&:nth-of-type(1)': { left: 0 },

  ...getConditionalObject(isFeeProviderColumnVisible, {
    '&:nth-of-type(2)': { left: `${COLUMN_WIDTH}px` },
  }),

  ...getConditionalObject(isOpened, {
    '&:nth-last-of-type(1)': {
      width: '70px !important',
      right: 0,

      '&::after': { left: 0 },
    },

    '&:nth-last-of-type(2)': {
      borderRight: 'none !important',
    },
  }),
});

const getStickyColumnsStyles = ({ isOpened, isFeeProviderColumnVisible }) => ({
  '& tr > *': _.merge(getColumnOffsetStyles({ isOpened, isFeeProviderColumnVisible }), {
    [getStickyColumnSelector({ isOpened, isFeeProviderColumnVisible })]: stickyColumnStyles,
  }),
});

const getScrollableColumnsStyles = ({ isOpened, isFeeProviderColumnVisible }) => ({
  '& table': {
    width: 'max-content',
  },

  '& tr > *': {
    width: `${COLUMN_WIDTH}px !important`,

    '&[colspan="2"]': {
      width: `${MERGED_COLUMN_WIDTH}px !important`,
    },
  },

  [mediaQuery.wideDesktop]: getStickyColumnsStyles({ isOpened, isFeeProviderColumnVisible }),

  [mediaQuery.belowWideDesktop]: getStickyColumnsStyles({
    isOpened,
    isFeeProviderColumnVisible: false,
  }),
});

const getScrollableTableStyles = ({ isOpened, isFeeProviderColumnVisible }) => ({
  [mediaQuery.tabletAndUp]: {
    [mediaQuery.belowLargeScreen]: getScrollableColumnsStyles({
      isOpened,
      isFeeProviderColumnVisible,
    }),
  },
});

export const getTableStyles = ({
  isFeeProviderColumnVisible = true,
  isNonBorrowerPaidFieldsIncluded = true,
  isFinancedAmountColumnVisible = true,
  isOpened,
  isEmpty,
}) => {
  const isScrollable = !isEmpty && isNonBorrowerPaidFieldsIncluded;

  const closedFirstColumnWidth = isFeeProviderColumnVisible ? '400px' : '500px';

  return {
    borderRadius: '5px',
    border: '1px solid',
    borderColor: ({ palette }) => palette.border.default,
    overflowY: 'hidden',

    '& .helper-text': {
      marginTop: '-5px',
    },

    '& tr ': {
      height: 48,
      position: 'relative',

      ...pendingRowCellsStyles,
    },

    ...deletedPendingRowStyles,

    ...getConditionalObject(isScrollable, () =>
      getScrollableTableStyles({ isOpened, isFeeProviderColumnVisible }),
    ),

    '& th, td': {
      '&:not(:nth-last-of-type(1))': {
        borderRight: '1px solid',
        borderBottom: '1px solid',
        borderColor: ({ palette }) => palette.border.default,
      },

      '& [data-is-empty="true"][data-is-changed="false"]': {
        display: 'none',
      },

      '&:nth-of-type(1)': {
        width: isOpened ? '200px' : closedFirstColumnWidth,
      },

      '&:nth-of-type(2)': {
        width: isOpened ? '200px' : 'auto',
      },

      '&[colspan="2"]': {
        padding: 0,

        '& > div': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px 0',

          '& > span': {
            width: '50%',
            padding: '0 16px',

            '&:nth-of-type(1)': {
              height: isOpened ? 'auto' : '20px',
              borderRight: '1px solid',
              borderColor: ({ palette }) => palette.border.default,
            },
          },
        },
      },

      ...getConditionalObject(isNonBorrowerPaidFieldsIncluded, {
        [`&:nth-last-of-type(-n + ${isFinancedAmountColumnVisible ? 2 : 1})`]: {
          width: '0.1%',
          whiteSpace: 'nowrap',

          '&.financed-amount-cell': {
            minWidth: '160px',
          },
        },
      }),
    },

    '& thead th, & thead td': {
      '&[colspan="2"] > div:not(:nth-last-of-type(1))': {
        borderBottom: '1px solid',
        borderColor: ({ palette }) => palette.border.default,
      },
    },

    '& tbody th, tbody td': {
      position: 'relative',
      color: ({ palette }) => palette.text.secondary,
    },

    ...mobileFeeCardStyles,
  };
};

export const nameAmountTableStyles = {
  borderRadius: '5px',
  border: '1px solid',
  borderColor: ({ palette }) => palette.border.default,

  '& tr': {
    height: 48,
    position: 'relative',

    ...pendingRowCellsStyles,
  },

  ...deletedPendingRowStyles,

  '& th, td': {
    position: 'relative',
    borderBottom: '1px solid',
    borderColor: ({ palette }) => palette.border.default,
  },

  '& tbody th, tbody td': {
    color: ({ palette }) => palette.text.secondary,
  },

  [mediaQuery.mobile]: {
    overflowX: 'hidden',

    '& table': { width: '100% !important', tableLayout: 'fixed' },

    '& tbody tr': {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '8px',
      alignItems: 'baseline',
      minHeight: 'auto',
      height: 'auto',
    },

    '& tbody td': {
      display: 'block',
      width: 'auto !important',
      minWidth: 0,
      border: 'none !important',
      padding: '6px 0 !important',
      whiteSpace: 'normal !important',

      '& .truncate': {
        whiteSpace: 'normal !important',
        overflow: 'visible',
        textOverflow: 'clip',
        minWidth: 0,
      },

      '& .float-right': { float: 'none !important' },
    },

    '& tbody td:first-of-type': { flex: '1 1 auto', minWidth: 0 },

    '& tbody td:last-of-type': { flex: '0 0 auto', textAlign: 'right' },
  },
};

export const addButtonStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: 'fit-content',
  height: 'fit-content',
  justifySelf: 'end',
  marginRight: '24px',
  fontSize: '1.4rem',
  fontWeight: 400,
  color: COLOR_PALETTE.BRAND[6],
  textTransform: 'none',

  '&[disabled]': {
    opacity: 0.7,
    color: COLOR_PALETTE.BRAND[6],
  },
};
